import React, { PureComponent, createRef } from "react";
import {
  Chart,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartArea,
  ChartTooltip,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesLabels,
  ChartSeriesLabelsFrom,
  ChartSeriesLabelsTo,
  ChartPlotArea,
} from "@progress/kendo-react-charts";
import "hammerjs";
import { formatNumber } from "@telerik/kendo-intl";
import Images from "../../Assets/Images";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import EmailService from "../../Services/EmailService";
import EmailChartPopup from "../Common/EmailChartPopup";

export default class FullTornadoChart extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      fullTornado: this.props.tornodoResponse,
      isOpportunity: this.props.isOpportunity,
    };
    // Create a reference to the chart container
    this.chartContainerRef = createRef();
    // Create a reference specifically for the Chart
    this.chartRef = createRef();
  }
  
  // Update state when props change
  componentDidUpdate(prevProps) {
    if (prevProps.isOpportunity !== this.props.isOpportunity ||
        prevProps.tornodoResponse !== this.props.tornodoResponse) {
      this.setState({
        fullTornado: this.props.tornodoResponse,
        isOpportunity: this.props.isOpportunity
      });
    }
  }

  // Helper function to render chart to canvas
  renderChartToCanvas = (chartTitle) => {
    try {
      console.log("Rendering chart to canvas...");
      
      // Get chart data from state instead of props directly
      const data = this.state.fullTornado || [];
      if (data.length === 0) {
        console.error("No data available for chart");
        return null;
      }
      
      // Create a canvas element with increased width
      const canvas = document.createElement('canvas');
      canvas.width = 1200; // Increased width to accommodate labels
      
      // Calculate the minimum required height based on the number of data points
      const totalDataPoints = data.length;
      const baseHeight = 150; // Base height for title, margins, etc.
      
      // Minimum height needed per data point to ensure readability
      // This is based on the bar height (30px) plus spacing (25px) plus extra for text wrapping
      const minHeightPerItem = 60;
      
      // Calculate the absolute minimum height needed to display all data points
      // This ensures no data truncation regardless of the number of records
      const requiredHeight = baseHeight + (totalDataPoints * minHeightPerItem);
      
      // Use either the required height or a minimum of 625px, whichever is larger
      canvas.height = Math.max(625, requiredHeight);
      
      console.log(`Canvas height set to ${canvas.height}px for ${totalDataPoints} data points`);
      const context = canvas.getContext('2d');
      
      // Set background to white for better visibility in exports
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw title
      context.fillStyle = '#000000';
      context.font = 'bold 24px Arial';
      context.textAlign = 'center';
      const updatedTitle = this.props.optionSelection == "all" ? chartTitle : chartTitle + ' - Top 10';
      context.fillText(updatedTitle, canvas.width / 2, 30);
      
      // Calculate max value for scaling
      let maxValue = 0;
      data.forEach(item => {
        const maxVal = Math.abs(parseFloat(item.max) || 0);
        const minVal = Math.abs(parseFloat(item.min) || 0);
        if (maxVal > maxValue) maxValue = maxVal;
        if (minVal > maxValue) maxValue = minVal;
      });
      
      // Add some margin
      maxValue = maxValue * 1.1;
      
      // Calculate scale factor - ensure bars are visible
      const scaleX = Math.min((canvas.width - 300) / (maxValue * 2), 0.9);
      
      // Draw bars
      const barHeight = 30;
      const startY = 80;
      let currentY = startY;
      const centerX = canvas.width / 2;
      
      // Draw center line - extend to match the new canvas height
      context.strokeStyle = '#999999';
      context.beginPath();
      context.moveTo(centerX, 60);
      // Make sure the line extends to the bottom of the canvas with some padding
      context.lineTo(centerX, canvas.height - 20);
      context.stroke();
      
      // Helper function to wrap text
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(' ');
        let line = '';
        let lines = [];
        
        for(let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        
        lines.push(line);
        
        for(let i = 0; i < lines.length; i++) {
          context.fillText(lines[i], x, y + (i * lineHeight));
        }
        
        return lines.length;
      };
      
      // Draw data
      data.forEach((item, index) => {
        const minVal = parseFloat(item.min);
        const maxVal = parseFloat(item.max);
        const barWidth = Math.abs(maxVal - minVal) * scaleX;
        
        // Calculate bar position - handle all cases for negative values
        let barStartX;
        
        // For opportunity chart, ensure proper positioning for all negative value scenarios
        if (this.state.isOpportunity) {
          // Case 1: Both min and max are negative
          if (minVal < 0 && maxVal < 0) {
            barStartX = centerX + (maxVal * scaleX); // Start from the less negative value (closer to center)
          }
          // Case 2: min is 0 (or positive) and max is negative
          else if (minVal >= 0 && maxVal < 0) {
            barStartX = centerX + (maxVal * scaleX); // Start from the negative value
          }
          // Case 3: min is negative and max is 0 (or positive)
          else if (minVal < 0 && maxVal >= 0) {
            barStartX = centerX + (minVal * scaleX); // Start from the negative value
          }
          // Case 4: Both are positive (unlikely for opportunity chart but handle it)
          else {
            barStartX = centerX + (minVal * scaleX);
          }
        } else {
          // Standard positioning for non-opportunity chart
          barStartX = centerX + (minVal * scaleX);
        }
        
        // Draw bar
        context.fillStyle = this.state.isOpportunity ? '#BEBE12' : '#018cf4';
        context.fillRect(barStartX, currentY, barWidth, barHeight);
        
        // Draw min/max values
        context.fillStyle = '#000000';
        context.font = '12px Arial';
        context.textAlign = 'right';
        context.fillText(formatNumber(minVal, "##,#"), barStartX - 5, currentY + barHeight / 2 + 4);
        
        // Draw max value
        context.textAlign = 'left';
        context.fillText(formatNumber(maxVal, "##,#"), barStartX + barWidth + 5, currentY + barHeight / 2 + 4);
        
        // Draw category label
        let categoryText = item.categories;
        if (typeof categoryText === 'string' && categoryText.includes('##')) {
          const parts = categoryText.split('##');
          const riskNumber = parts[0];
          const riskTitle = parts[1];
          
          const titleMaxWidth = 200;
          const lineHeight = 14;
          
          if (this.state.isOpportunity) {
            // For opportunity chart, position risk number at a fixed position from the center line
            // This ensures consistent alignment for all risk numbers
            context.fillStyle = '#000000';
            context.textAlign = 'left';
            const riskNumberX = centerX + 100; // Fixed position for all risk numbers
            context.fillText(riskNumber, riskNumberX, currentY + barHeight / 2 + 4);
            
            // Get width of the risk number
            const riskNumberWidth = context.measureText(riskNumber).width;
            
            // Draw title after the risk number
            context.fillStyle = '#BEBE12'; // Yellow color for opportunity
            context.textAlign = 'left';
            const titleX = riskNumberX + riskNumberWidth + 5;
            const titleY = currentY + barHeight / 2 + 4; // Align with the bar
            
            const linesCount = wrapText(context, riskTitle, titleX, titleY, titleMaxWidth, lineHeight);
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          } else {
            // For threat chart, position risk number and title on the left side
            
            // Draw risk number on the left side of the bar
            context.fillStyle = '#000000';
            context.textAlign = 'right';
            context.fillText(riskNumber, centerX - 10, currentY + barHeight / 2 + 4);
            
            // Draw title after the risk number (positioned to avoid overlapping with values)
            context.fillStyle = '#018cf4'; // Blue color for threat
            context.textAlign = 'left';
            
            // For threat chart, position all titles at a fixed position from the center line
            // This ensures consistent alignment and prevents overlap with min values
            const titleX = centerX - 300; // Fixed position for all titles, far enough to avoid any min values
            const linesCount = wrapText(context, riskTitle, titleX, currentY + barHeight / 2 + 4, titleMaxWidth, lineHeight);
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          }
          
          // Extra space already added in the conditional blocks above
        } else {
          context.fillStyle = '#000000';
          context.textAlign = 'center';
          context.fillText(categoryText, centerX, currentY + barHeight + 15);
        }
        
        currentY += barHeight + 25; // Increased vertical spacing
      });
      
      // Add footer
      context.fillStyle = '#000000';
      context.font = '10px Arial';
      context.textAlign = 'center';
      // context.fillText('Riskwell - Full Tornado Chart Export', canvas.width / 2, canvas.height - 10);
      
      return canvas;
    } catch (error) {
      console.error("Error rendering chart to canvas:", error);
      return null;
    }
  };
  
  // Helper function to download chart as image
  downloadChartAsImage = async () => {
    try {
      console.log("Starting PNG export...");
      
      // Get chart title from props or use default
      const chartTitle = this.props.title || 'Full Tornado Chart';
      // Render chart to canvas
      const canvas = this.renderChartToCanvas(chartTitle);
      if (!canvas) {
        throw new Error("Failed to render chart to canvas");
      }
      
      // Convert canvas to PNG
      const imgData = canvas.toDataURL('image/png');
      
      // Create a download link
      const downloadLink = document.createElement('a');
      downloadLink.href = imgData;
      downloadLink.download = `${chartTitle}.png`;
      downloadLink.style.display = 'none';
      
      // Add to document, click, and remove
      document.body.appendChild(downloadLink);
      console.log("Download link created for PNG");
      downloadLink.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(downloadLink);
      }, 100);
      
    } catch (error) {
      console.error("Error in PNG export:", error);
      alert("Failed to generate PNG. Please try again or contact support.");
    }
  };

  // Helper function to render a subset of data to canvas
  renderChartSubsetToCanvas = (chartTitle, dataSubset) => {
    try {
      console.log(`Rendering chart subset with ${dataSubset.length} records to canvas...`);
      
      // Create a canvas element with increased width
      const canvas = document.createElement('canvas');
      canvas.width = 1200; // Increased width to accommodate labels
      
      // Calculate the minimum required height based on the number of data points
      const totalDataPoints = dataSubset.length;
      const baseHeight = 150; // Base height for title, margins, etc.
      
      // Minimum height needed per data point to ensure readability
      const minHeightPerItem = 60;
      
      // Calculate the absolute minimum height needed to display all data points
      const requiredHeight = baseHeight + (totalDataPoints * minHeightPerItem);
      
      // Use either the required height or a minimum of 625px, whichever is larger
      canvas.height = Math.max(625, requiredHeight);
      
      console.log(`Canvas height set to ${canvas.height}px for ${totalDataPoints} data points`);
      const context = canvas.getContext('2d');
      
      // Set background to white for better visibility in exports
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw title
      context.fillStyle = '#000000';
      context.font = 'bold 24px Arial';
      context.textAlign = 'center';
      context.fillText(chartTitle, canvas.width / 2, 30);
      
      // Calculate max value for scaling
      let maxValue = 0;
      dataSubset.forEach(item => {
        const maxVal = Math.abs(parseFloat(item.max) || 0);
        const minVal = Math.abs(parseFloat(item.min) || 0);
        if (maxVal > maxValue) maxValue = maxVal;
        if (minVal > maxValue) maxValue = minVal;
      });
      
      // Add some margin
      maxValue = maxValue * 1.1;
      
      // Calculate scale factor - ensure bars are visible
      const scaleX = Math.min((canvas.width - 300) / (maxValue * 2), 0.9);
      
      // Draw bars
      const barHeight = 30;
      const startY = 80;
      let currentY = startY;
      const centerX = canvas.width / 2;
      
      // Draw center line - extend to match the new canvas height
      context.strokeStyle = '#999999';
      context.beginPath();
      context.moveTo(centerX, 60);
      // Make sure the line extends to the bottom of the canvas with some padding
      context.lineTo(centerX, canvas.height - 20);
      context.stroke();
      
      // Helper function to wrap text
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(' ');
        let line = '';
        let lines = [];
        
        for(let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        
        lines.push(line);
        
        for(let i = 0; i < lines.length; i++) {
          context.fillText(lines[i], x, y + (i * lineHeight));
        }
        
        return lines.length;
      };
      
      // Draw data
      dataSubset.forEach((item, index) => {
        const minVal = parseFloat(item.min);
        const maxVal = parseFloat(item.max);
        const barWidth = Math.abs(maxVal - minVal) * scaleX;
        
        // Calculate bar position - handle all cases for negative values
        let barStartX;
        
        // For opportunity chart, ensure proper positioning for all negative value scenarios
        if (this.state.isOpportunity) {
          // Case 1: Both min and max are negative
          if (minVal < 0 && maxVal < 0) {
            barStartX = centerX + (maxVal * scaleX); // Start from the less negative value (closer to center)
          }
          // Case 2: min is 0 (or positive) and max is negative
          else if (minVal >= 0 && maxVal < 0) {
            barStartX = centerX + (maxVal * scaleX); // Start from the negative value
          }
          // Case 3: min is negative and max is 0 (or positive)
          else if (minVal < 0 && maxVal >= 0) {
            barStartX = centerX + (minVal * scaleX); // Start from the negative value
          }
          // Case 4: Both are positive (unlikely for opportunity chart but handle it)
          else {
            barStartX = centerX + (minVal * scaleX);
          }
        } else {
          // Standard positioning for non-opportunity chart
          barStartX = centerX + (minVal * scaleX);
        }
        
        // Draw bar
        context.fillStyle = this.state.isOpportunity ? '#BEBE12' : '#018cf4';
        context.fillRect(barStartX, currentY, barWidth, barHeight);
        
        // Draw min/max values
        context.fillStyle = '#000000';
        context.font = '12px Arial';
        context.textAlign = 'right';
        context.fillText(formatNumber(minVal, "##,#"), barStartX - 5, currentY + barHeight / 2 + 4);
        
        // Draw max value
        context.textAlign = 'left';
        context.fillText(formatNumber(maxVal, "##,#"), barStartX + barWidth + 5, currentY + barHeight / 2 + 4);
        
        // Draw category label
        let categoryText = item.categories;
        if (typeof categoryText === 'string' && categoryText.includes('##')) {
          const parts = categoryText.split('##');
          const riskNumber = parts[0];
          const riskTitle = parts[1];
          
          const titleMaxWidth = 200;
          const lineHeight = 14;
          
          if (this.state.isOpportunity) {
            // For opportunity chart, position risk number at a fixed position from the center line
            // This ensures consistent alignment for all risk numbers
            context.fillStyle = '#000000';
            context.textAlign = 'left';
            const riskNumberX = centerX + 100; // Fixed position for all risk numbers
            context.fillText(riskNumber, riskNumberX, currentY + barHeight / 2 + 4);
            
            // Get width of the risk number
            const riskNumberWidth = context.measureText(riskNumber).width;
            
            // Draw title after the risk number
            context.fillStyle = '#BEBE12'; // Yellow color for opportunity
            context.textAlign = 'left';
            const titleX = riskNumberX + riskNumberWidth + 5;
            const titleY = currentY + barHeight / 2 + 4; // Align with the bar
            
            const linesCount = wrapText(context, riskTitle, titleX, titleY, titleMaxWidth, lineHeight);
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          } else {
            // For threat chart, position risk number and title on the left side
            
            // Draw risk number on the left side of the bar
            context.fillStyle = '#000000';
            context.textAlign = 'right';
            context.fillText(riskNumber, centerX - 10, currentY + barHeight / 2 + 4);
            
            // Draw title after the risk number (positioned to avoid overlapping with values)
            context.fillStyle = '#018cf4'; // Blue color for threat
            context.textAlign = 'left';
            
            // For threat chart, position all titles at a fixed position from the center line
            // This ensures consistent alignment and prevents overlap with min values
            const titleX = centerX - 300; // Fixed position for all titles, far enough to avoid any min values
            const linesCount = wrapText(context, riskTitle, titleX, currentY + barHeight / 2 + 4, titleMaxWidth, lineHeight);
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          }
          
          // Extra space already added in the conditional blocks above
        } else {
          context.fillStyle = '#000000';
          context.textAlign = 'center';
          context.fillText(categoryText, centerX, currentY + barHeight + 15);
        }
        
        currentY += barHeight + 25; // Increased vertical spacing
      });
      
      return canvas;
    } catch (error) {
      console.error("Error rendering chart subset to canvas:", error);
      return null;
    }
  };

  // Helper function to download chart as PDF
  downloadChartAsPDF = async () => {
    try {
      console.log("Starting PDF export with pagination...");
      
      // Get chart title from props or use default
      const chartTitle = this.props.title || 'Full Tornado Chart';
      
      // Get chart data from state
      const data = this.state.fullTornado || [];
      if (data.length === 0) {
        throw new Error("No data available for chart");
      }
      
      // Create PDF with appropriate orientation
      const orientation = 'landscape'; // Use landscape for better readability of tornado charts
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4'
      });
      
      // Set PDF metadata
      pdf.setProperties({
        title: chartTitle,
        subject: 'Full Tornado Chart Export',
        author: 'Riskwell',
        keywords: 'tornado, chart, export',
        creator: 'Riskwell Application'
      });
      
      // Get page dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Add date to all pages
      const currentDate = new Date().toLocaleDateString();
      
      // Define records per page
      const recordsPerPage = 10;
      
      // Calculate number of pages needed
      const totalPages = Math.ceil(data.length / recordsPerPage);
      console.log(`Total pages needed: ${totalPages} for ${data.length} records`);
      
      // Process each page
      for (let pageNum = 0; pageNum < totalPages; pageNum++) {
        // Add a new page for all pages except the first one
        if (pageNum > 0) {
          pdf.addPage();
        }
        
        // Calculate start and end indices for this page
        const startIdx = pageNum * recordsPerPage;
        const endIdx = Math.min(startIdx + recordsPerPage, data.length);
        
        // Get subset of data for this page
        const dataSubset = data.slice(startIdx, endIdx);
        const updatedTitle = this.props.optionSelection == "all" ? chartTitle : chartTitle + ' - Top 10';      
        // Render this subset to canvas
        const canvas = this.renderChartSubsetToCanvas(updatedTitle, dataSubset);
        if (!canvas) {
          throw new Error(`Failed to render chart for page ${pageNum + 1}`);
        }
        
        // Convert canvas to image for PDF
        const imgData = canvas.toDataURL('image/png', 1.0);
        
        // Add title to PDF
        pdf.setFontSize(20);
        pdf.setTextColor(0, 0, 0);
        
        // Add date
        pdf.setFontSize(10);
        pdf.text(`Generated: ${currentDate}`, pdfWidth - 20, 10, { align: 'right' });
        
        // Calculate dimensions for the image
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        // Use a larger portion of the page for the chart
        const maxWidth = pdfWidth * 0.9; // Use 90% of page width
        const maxHeight = pdfHeight * 0.8; // Use 80% of page height
        
        const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;
        
        // Center the image
        const imgX = (pdfWidth - scaledWidth) / 2;
        const imgY = 25; // Position below the title
        
        // Add the chart image
        pdf.addImage(imgData, 'PNG', imgX, imgY, scaledWidth, scaledHeight);
        
        // Add page number at the bottom
        pdf.setFontSize(10);
        pdf.text(`Page ${pageNum + 1} of ${totalPages}`, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
      }
      
      console.log("PDF created with pagination, attempting to download...");
      
      // Save the PDF
      pdf.save(`${chartTitle}.pdf`);
      
    } catch (error) {
      console.error('Error in PDF export process:', error);
      alert("Failed to generate PDF. Please try again or contact support.");
    }
  };

  // Method to export chart as PNG
  onImageExportClick = () => {
    this.downloadChartAsImage();
  };

  // Method to export chart as PDF
  onPDFExportClick = () => {
    this.downloadChartAsPDF();
  };
  
  // Method to export chart as email
  onEmailExportClick = async (emailId, message) => {
    try {
      console.log("Starting email export...");
      
      // Get chart title from props or use default
      const chartTitle = this.props.title || 'Full Tornado Chart';
      
      // Render chart to canvas
      const canvas = this.renderChartToCanvas(chartTitle);
      if (!canvas) {
        throw new Error("Failed to render chart to canvas");
      }
      
      // Convert canvas to image for email
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Send email using EmailService
      const result = await EmailService.sendChartEmail(
        imgData,
        0, // widgetId
        emailId,
        message,
        `${chartTitle}.png`
      );
      
      if (result) {
        console.log('Email sent successfully');
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };
  render() {
    const tooltipRender = (props) => (
      <div>
        Min : {props.point.value && formatNumber(Number(props.point.value.from), "##,#")}
        <br />
        Max : {props.point.value && formatNumber(Number(props.point.value.to), "##,#")}
      </div>
    );

    const labelContentFrom = (e) =>{
      return "";//`${formatNumber(Number(e.value.from), "##,#")}`;
    } 

    const labelContentTo = (e) =>{
      return "";//`${formatNumber(Number(e.value.to), "##,#")} `;
    } 
    const labelContenXAxis=(e) => `${formatNumber(Number(e.value), "##,#")}`;

    let data=[];
    this.props.tornodoResponse&&this.props.tornodoResponse.map((item)=>{
      data.push(
        {
          "categories": item.categories.includes("##")?item.categories.toString().split('##')[0] + " " + item.categories.toString().split('##')[1]:item.categories,//"RSK-000711## (TSP120PS250)",
          "value": item.value,
          "min": item.min,
          "max": item.max,
          "meanVal": item.meanVal,
          "percentile": item.percentile,
          "percentage": item.percentage
        }
      )
    });
    const ChartContainer = () => (
      <Chart
      // pannable={{ lock: "y" }}
      // zoomable={{
      //   mousewheel: { lock: "y" },                                                                                                         
      //   selection: { lock: "y" },
      // }}
      >
        {/* <ChartTitle text="Average Weather Conditions" /> */}
        <ChartArea background="transparent" height={data.length > 8 ? data.length*40 : 400}/>
        <ChartSeries>
          {!this.state.isOpportunity ? (
            <ChartSeriesItem
              color="#018cf4"
              background="transparent"
              type="rangeBar"
              data={data}
              fromField="min"
              toField="max"
              categoryField="categories"
              segmentSpacing={1}
             

              gap={1}
              dynamicHeight={true}
              spacing={1}
              size={5}
              minSize={100}
            >
              <ChartSeriesLabels>
                <ChartSeriesLabelsFrom
                  background="transparent"
                  color="#ffffff"
                  content={labelContentFrom}
                  position="center"
                  margin={{
                    top: 3                                                                                                   ,
                    right: 70
                  }}
                />
                <ChartSeriesLabelsTo
                  background="transparent"
                  color="#ffffff"
                  content={labelContentTo}
                  position="center"
                  // margin={-10}
                  margin={{
                    top: 3                                                                                                   ,
                    left: 70
                  }}
                />
              </ChartSeriesLabels>
            </ChartSeriesItem>
          ) : (
            <ChartSeriesItem
              color="#BEBE12"
              background="transparent"
              type="rangeBar"
              data={data}
              fromField="min"
              toField="max"
              categoryField="categories"
              segmentSpacing={1}
              dynamicHeight={false}
            >
              <ChartSeriesLabels>
                <ChartSeriesLabelsFrom
                  background="transparent"
                  content={labelContentFrom}
                  color="#ffffff"
                  // position="outsideEnd"
                  position="center"
                  margin={{
                    top: 3                                                                                                   ,
                    left: 70
                  }}
                  minSize={100}
                />
                <ChartSeriesLabelsTo
                  background="transparent"
                  content={labelContentTo}
                  color="#ffffff"
                  position="center"
                  // margin={-10}
                  margin={{
                    top: 3                                                                                                   ,
                    right: 70
                  }}
                />
              </ChartSeriesLabels>
            </ChartSeriesItem>
          )}
        </ChartSeries>
        <ChartValueAxis skip={4}>
          <ChartValueAxisItem
            labels={{
              rotation: "-45",
              format: "{0:##,#}",
            }}
            // content={labelContenXAxis}
            // margin={{
            //   // top: 2,
            //   // right: 2,
            //   // bottom: 2,
            //   left: 25,
            // }}
            // padding={40}
            color="#ffffff"
            // content={labelContenXAxis} 
            line={{
              width: "1",
              color: "#555555",
              visible: true,
            }}
          ></ChartValueAxisItem>
        </ChartValueAxis>
        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            background="transparent"
            line={{
              width: "1",
              color: "#555555",
              visible: true,
              // dashType:"solid"
            }}
            color="#ffffff"
            // startAngle={45}
            // labels={{
            //   rotation: "45",
            //   format: "d",
            // }}
          />
        </ChartCategoryAxis>
        <ChartTooltip render={tooltipRender} />
      </Chart>
    );

    return (
      <div className="butterflyChartCont" ref={this.chartContainerRef}>
        <ul className="WidgetActions">
          <li title="PNG Download" onClick={this.onImageExportClick}>
            <a>
              <img src={Images.DOWNLOAD} alt="Download" width="14" />
            </a>
          </li>
          <li title="PDF Download" onClick={this.onPDFExportClick}>
            <a>
              <img src={Images.PDF} alt="Download" width="14" />
            </a>
          </li>
          <li>
            <EmailChartPopup
              onSendEmail={this.onEmailExportClick}
              widgetId={0}
              widgetName={this.props.title || 'Full Tornado Chart'}
            />
          </li>
        </ul>
        {data.length != 0 && <ChartContainer />}
      </div>
    );
  }
}
